import type { ModelDocument } from "../shared/contracts.js";
import { stableStringify } from "./operations.js";
/** Synchronous browser-safe SHA-256. This is content identity, never a password KDF. */
export function sha256(text: string): string {
  const bytes = new TextEncoder().encode(text),
    size = Math.ceil((bytes.length + 9) / 64) * 64,
    data = new Uint8Array(size);
  data.set(bytes);
  data[bytes.length] = 128;
  const view = new DataView(data.buffer),
    bits = bytes.length * 8;
  view.setUint32(size - 8, Math.floor(bits / 0x100000000));
  view.setUint32(size - 4, bits >>> 0);
  const k = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  const h = [
      0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
      0x1f83d9ab, 0x5be0cd19,
    ],
    w = new Uint32Array(64),
    rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let offset = 0; offset < size; offset += 64) {
    for (let t = 0; t < 16; t++) w[t] = view.getUint32(offset + t * 4);
    for (let t = 16; t < 64; t++) {
      const x = w[t - 15],
        y = w[t - 2],
        s0 = rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3),
        s1 = rotr(y, 17) ^ rotr(y, 19) ^ (y >>> 10);
      w[t] = (w[t - 16] + s0 + w[t - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hv] = h;
    for (let t = 0; t < 64; t++) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25),
        ch = (e & f) ^ (~e & g),
        t1 = (hv + s1 + ch + k[t] + w[t]) >>> 0,
        s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22),
        maj = (a & b) ^ (a & c) ^ (b & c),
        t2 = (s0 + maj) >>> 0;
      hv = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    [a, b, c, d, e, f, g, hv].forEach((v, i) => (h[i] = (h[i] + v) >>> 0));
  }
  return h.map((v) => v.toString(16).padStart(8, "0")).join("");
}
export function semanticDigest(document: ModelDocument): string {
  const { activeRepresentation, appearance, ...model } = document.model;
  const semantic = {
    format: document.format,
    formatVersion: document.formatVersion,
    model,
    representations: document.representations
      .map(({ createdAt, ...r }) => ({
        ...r,
        objectIds: [...r.objectIds].sort(),
      }))
      .sort((a, b) => a.representationId.localeCompare(b.representationId)),
    objects: [...document.objects].sort((a, b) =>
      a.objectId.localeCompare(b.objectId),
    ),
    relationships: [...document.relationships].sort((a, b) =>
      a.relationshipId.localeCompare(b.relationshipId),
    ),
    mappings: [...document.mappings].sort((a, b) =>
      a.mappingId.localeCompare(b.mappingId),
    ),
    dependencies: document.dependencies,
    sourceIdentities: document.sourceIdentities,
    extensions: document.extensions,
  };
  return sha256(stableStringify(semantic));
}
